import * as AlertDialogPrimitive from '@radix-ui/react-alert-dialog';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Facebook, Info, Loader2, RefreshCw, ShieldCheck } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { QueryKey } from '@/Config/api/queryKey';
import {
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/Domain/components/Shared/AlertDialog/AlertDialog';
import { Badge } from '@/Domain/components/Shared/Badge/Badge';
import { Button, IconButton } from '@/Domain/components/Shared/Button/Button';
import { Separator } from '@/Domain/components/Shared/Separator/Separator';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/Domain/components/Shared/Sheet/sheet';
import CloseIcon from '@/Domain/components/icons/Close.icon';
import { MessengerIcon } from '@/Domain/components/icons/Messenger.icon';
import { PostHogEventEnum } from '@/Domain/enum/PostHogEvent.enum';
import { usePostHogAnalytics } from '@/Domain/hooks/App/usePostHog/usePostHogAnalytics.hook';
import { getCurrentScenarioId } from '@/context';

const FACEBOOK_INTEGRATION_NAME = 'facebook' as const;
const FACEBOOK_QUERY_KEY = [QueryKey.integration.get, FACEBOOK_INTEGRATION_NAME] as const;

const FACEBOOK_OAUTH_BASE = 'https://www.facebook.com/v18.0/dialog/oauth';
const FACEBOOK_APP_ID = '775209690952444';
const FACEBOOK_REDIRECT_URI = 'https://meta.quickchat.ai/login';
// business_management lets the backend resolve Business-owned Pages (Tier-3 discovery in discover_messenger_pages); mirrors the pre-4086 modal.
const FACEBOOK_SCOPES = [
  'email',
  'pages_show_list',
  'pages_read_engagement',
  'pages_messaging',
  'pages_manage_metadata',
  'business_management',
  'public_profile',
].join(',');
const POPUP_FEATURES = 'width=600,height=800';
const POPUP_POLL_INTERVAL_MS = 500;

interface FacebookIntegrationParams {
  facebook_bot_active?: boolean;
  facebook_page_id?: string | null;
  facebook_page_name?: string | null;
  facebook_app_secret?: string | null;
  facebook_access_token?: string | null;
  facebook_verify_token?: string | null;
}

interface FacebookIntegrationResponse {
  name: 'facebook';
  params: FacebookIntegrationParams;
}

interface MessengerSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function buildOAuthUrl(scenarioId: string): string {
  const state = encodeURIComponent(JSON.stringify({ scenario_id: scenarioId, type: 'messenger' }));
  const params = [
    `client_id=${FACEBOOK_APP_ID}`,
    `redirect_uri=${encodeURIComponent(FACEBOOK_REDIRECT_URI)}`,
    `scope=${FACEBOOK_SCOPES}`,
    `state=${state}`,
    'response_type=code',
    'display=popup',
    // re-prompt for any permission the user previously declined
    'auth_type=rerequest',
  ].join('&');
  return `${FACEBOOK_OAUTH_BASE}?${params}`;
}

export const MessengerSheet = ({ open, onOpenChange }: MessengerSheetProps) => {
  const { t } = useTranslation(['integration']);
  const { capture } = usePostHogAnalytics();
  const queryClient = useQueryClient();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const popupPollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const clearPopupPoll = useCallback(() => {
    if (popupPollRef.current !== null) {
      clearInterval(popupPollRef.current);
      popupPollRef.current = null;
    }
  }, []);

  useEffect(
    () => () => {
      clearPopupPoll();
    },
    [clearPopupPoll],
  );

  const { data, isLoading, refetch } = useQuery<FacebookIntegrationResponse>({
    queryKey: FACEBOOK_QUERY_KEY,
    enabled: open,
  });

  const pageId = data?.params?.facebook_page_id ?? null;
  const pageName = data?.params?.facebook_page_name ?? null;
  const isConnected = Boolean(pageId);

  const disconnectMutation = useMutation({
    mutationKey: [QueryKey.integration.facebookMessengerDelete],
    onSuccess: () => {
      queryClient.setQueryData<FacebookIntegrationResponse>(FACEBOOK_QUERY_KEY, {
        name: FACEBOOK_INTEGRATION_NAME,
        params: {},
      });
      setConfirmOpen(false);
    },
    onError: () => {
      toast.error(t('integration:messenger.disconnect_failed'));
    },
  });

  const handleConnect = useCallback(() => {
    const scenarioId = getCurrentScenarioId();
    const url = buildOAuthUrl(scenarioId);
    capture(PostHogEventEnum.MessengerOauthStarted);
    const popup = window.open(url, 'messenger_oauth', POPUP_FEATURES);
    if (!popup) {
      toast.error(t('integration:messenger.popup_blocked'));
      return;
    }

    clearPopupPoll();
    popupPollRef.current = setInterval(() => {
      // Guard against the host going away mid-poll (test teardown, page nav).
      if (typeof window === 'undefined' || !popup) {
        clearPopupPoll();
        return;
      }
      if (popup.closed) {
        clearPopupPoll();
        refetch();
      }
    }, POPUP_POLL_INTERVAL_MS);
  }, [capture, clearPopupPoll, refetch, t]);

  const handleDisconnect = useCallback(() => {
    disconnectMutation.mutate();
  }, [disconnectMutation]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="max-w-3xl w-full h-full" data-testid="messenger-sheet">
        <SheetHeader className="px-0.5">
          <div className="flex items-center gap-3">
            <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-muted">
              <MessengerIcon className="size-5" />
            </div>
            <div className="flex flex-col gap-0.5">
              <SheetTitle data-testid="messenger-title">
                {t('integration:messenger.title')}
              </SheetTitle>
              <SheetDescription data-testid="messenger-description">
                {t('integration:messenger.description')}
              </SheetDescription>
            </div>
          </div>
          <IconButton
            onClick={() => onOpenChange(false)}
            variant="secondary"
            data-testid="messenger-close-button"
          >
            <CloseIcon />
          </IconButton>
        </SheetHeader>

        <Separator />

        <div className="flex flex-col gap-6 overflow-y-auto px-0.5 flex-1">
          {isLoading ? (
            <div
              className="flex items-center justify-center py-12 text-muted-foreground"
              data-testid="messenger-loading"
            >
              <Loader2 className="animate-spin size-5" />
            </div>
          ) : isConnected ? (
            <div className="flex flex-col gap-5" data-testid="messenger-connected-view">
              <div
                className="rounded-xl border border-border bg-muted p-4 space-y-2"
                data-testid="messenger-connected-banner"
              >
                <Badge variant="success" size="md" data-testid="messenger-connected-badge">
                  {t('integration:messenger.connected_badge')}
                </Badge>
                <div className="min-w-0">
                  <p
                    className="text-sm font-medium text-foreground truncate m-0"
                    data-testid="messenger-connected-label"
                  >
                    {pageName ?? pageId}
                  </p>
                  <p
                    className="text-xs text-muted-foreground font-mono truncate m-0"
                    data-testid="messenger-page-id"
                  >
                    {t('integration:messenger.page_id', { id: pageId })}
                  </p>
                </div>
              </div>

              <div className="flex flex-col gap-3">
                <p
                  className="text-xs text-muted-foreground m-0"
                  data-testid="messenger-switch-hint"
                >
                  {t('integration:messenger.switch_hint')}
                </p>
                <div className="flex flex-wrap items-center gap-3">
                  <Button
                    variant="dark"
                    onClick={handleConnect}
                    data-testid="messenger-switch-page-button"
                  >
                    <RefreshCw className="size-4" />
                    {t('integration:messenger.switch_page')}
                  </Button>
                  <Button
                    variant="danger"
                    onClick={() => setConfirmOpen(true)}
                    disabled={disconnectMutation.isPending}
                    data-testid="messenger-disconnect-button"
                  >
                    {t('integration:messenger.disconnect')}
                  </Button>
                </div>
              </div>

              <Separator />

              <p
                className="flex items-start gap-2 text-xs text-muted-foreground m-0"
                data-testid="messenger-constraint-note"
              >
                <Info className="size-3.5 shrink-0 mt-0.5" />
                {t('integration:messenger.one_agent_one_page')}
              </p>
            </div>
          ) : (
            <div className="flex flex-col gap-6" data-testid="messenger-disconnected-view">
              <ol className="flex flex-col gap-3 list-none m-0 p-0">
                {(['step1', 'step2', 'step3'] as const).map((step, index) => (
                  <li
                    key={step}
                    className="flex items-start gap-3 rounded-xl border border-border p-4"
                    data-testid={`messenger-instructions-${step}`}
                  >
                    <span className="flex size-6 items-center justify-center rounded-full bg-muted text-xs font-semibold text-foreground shrink-0">
                      {index + 1}
                    </span>
                    <p className="text-sm text-foreground m-0 flex-1">
                      {t(`integration:messenger.instructions.${step}`)}
                    </p>
                  </li>
                ))}
              </ol>

              <Separator />

              <div className="flex flex-col gap-4">
                <div className="flex items-center">
                  <Button
                    variant="dark"
                    onClick={handleConnect}
                    data-testid="messenger-connect-button"
                  >
                    <Facebook className="size-4" />
                    {t('integration:messenger.connect')}
                  </Button>
                </div>

                <div className="flex flex-col gap-2">
                  <p
                    className="flex items-start gap-2 text-xs text-muted-foreground m-0"
                    data-testid="messenger-permissions-note"
                  >
                    <ShieldCheck className="size-3.5 shrink-0 mt-0.5" />
                    {t('integration:messenger.permissions_note')}
                  </p>
                  <p
                    className="flex items-start gap-2 text-xs text-muted-foreground m-0"
                    data-testid="messenger-constraint-note"
                  >
                    <Info className="size-3.5 shrink-0 mt-0.5" />
                    {t('integration:messenger.one_agent_one_page')}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        <AlertDialogPrimitive.Root open={confirmOpen} onOpenChange={setConfirmOpen}>
          <AlertDialogPrimitive.Overlay className="!mt-0 absolute inset-0 bg-black/30 z-20" />
          <AlertDialogPrimitive.Content className="absolute inset-0 flex items-center justify-center p-4 z-30 focus:outline-none">
            <div
              className="space-y-4 bg-card rounded-lg p-6 max-w-md w-full shadow-lg"
              data-testid="messenger-disconnect-dialog"
            >
              <AlertDialogHeader>
                <AlertDialogTitle>
                  {t('integration:messenger.disconnect_confirm.title')}
                </AlertDialogTitle>
              </AlertDialogHeader>
              <AlertDialogDescription>
                {t('integration:messenger.disconnect_confirm.description')}
              </AlertDialogDescription>
              <Separator className="my-4" />
              <AlertDialogFooter>
                <AlertDialogPrimitive.Cancel asChild>
                  <Button
                    size="md"
                    variant="secondary"
                    data-testid="messenger-disconnect-cancel-button"
                  >
                    {t('integration:messenger.disconnect_confirm.cancel')}
                  </Button>
                </AlertDialogPrimitive.Cancel>
                <Button
                  variant="danger"
                  size="md"
                  onClick={handleDisconnect}
                  disabled={disconnectMutation.isPending}
                  data-testid="messenger-disconnect-confirm-button"
                >
                  {disconnectMutation.isPending ? (
                    <Loader2 className="animate-spin size-4" />
                  ) : null}
                  {t('integration:messenger.disconnect_confirm.confirm')}
                </Button>
              </AlertDialogFooter>
            </div>
          </AlertDialogPrimitive.Content>
        </AlertDialogPrimitive.Root>
      </SheetContent>
    </Sheet>
  );
};
